<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';

import {
  formatIban,
  normalizeIban,
  type GetAccountDepositInformationResponseDtoOutput,
} from '@peanuds/shared';
import type { UserAccountBankDetails } from '@peanuds/shared-state';

import UiCopyToggleButton from './wrappers/UiCopyToggleButton.vue';
import CopyIcon from '../assets/icons/small-copy.svg?component';

interface Props {
  bankDetails?: UserAccountBankDetails | null;
  filled?: boolean;
}

interface DetailRow {
  key: string;
  label: string;
  value: string;
  schemes: string[];
}

const { bankDetails = null, filled = false } = defineProps<Props>();
const { t } = useTranslation();

const rows = computed(() => {
  const result: DetailRow[] = [];
  if (!bankDetails) return result;

  const appendInstructions = (
    instructions: GetAccountDepositInformationResponseDtoOutput['sepa'] | undefined,
    scheme: string,
    level = 0,
  ) => {
    if (!instructions) return;

    const appendField = (field: string, value: unknown, label = t(`ui.bank_details.${field}`)) => {
      if (typeof value !== 'string' || !value.trim()) return;

      const normalized = field === 'iban' ? normalizeIban(value) : value.trim();
      const key = `${level}:${field}`;
      const duplicate = result.find((row) => row.key === key && row.value === normalized);
      if (duplicate) {
        if (!duplicate.schemes.includes(scheme)) duplicate.schemes.push(scheme);
        return;
      }

      result.push({
        key,
        label: level > 0 ? t('ui.bank_details.intermediary_field', { field: label, level }) : label,
        value: normalized,
        schemes: [scheme],
      });
    };

    appendField('recipient', instructions.accountHolderName);
    appendField('iban', instructions.iban);
    appendField('account_number', instructions.accountNumber);
    Object.entries(instructions.routingCodes).forEach(([code, value]) => {
      if (code === 'bic' || code === 'swift') {
        appendField('bic', value);
        return;
      }
      appendField(
        `routing:${code}`,
        value,
        code === 'sort-code' ? t('ui.bank_details.sort_code') : code,
      );
    });
    appendField('bank_name', instructions.bankName);
    appendField('bank_address', instructions.bankAddress);
    appendField('currency', instructions.currency);
    appendField('country', instructions.country);
    appendField('reference', instructions.reference);
    appendInstructions(instructions.intermediaryAccount ?? undefined, scheme, level + 1);
  };

  if (bankDetails.depositInformation) {
    appendInstructions(bankDetails.depositInformation.sepa, 'SEPA');
    appendInstructions(bankDetails.depositInformation.swift, 'SWIFT');
  } else {
    appendInstructions(
      {
        accountHolderName: bankDetails.receiver,
        iban: bankDetails.iban,
        accountNumber: bankDetails.accountNumber,
        routingCodes: { bic: bankDetails.bic, swift: bankDetails.swiftCode },
        bankName: bankDetails.bankName,
        bankAddress: bankDetails.bankAddress,
        country: '',
        currency: '',
      },
      '',
    );
  }

  return result.map((row) => ({
    ...row,
    label:
      row.schemes.length === 1 && row.schemes[0]
        ? t('ui.bank_details.scheme_field', { field: row.label, scheme: row.schemes[0] })
        : row.label,
  }));
});

const copyState = ref<'idle' | 'loading' | 'success'>('idle');
let copyTimeout: ReturnType<typeof setTimeout> | undefined;
onUnmounted(() => clearTimeout(copyTimeout));

const copyInformation = async () => {
  if (!rows.value.length || copyState.value === 'loading') return;
  copyState.value = 'loading';
  try {
    await navigator.clipboard.writeText(
      rows.value.map((row) => `${row.label}: ${row.value}`).join('\n'),
    );
    copyState.value = 'success';
    clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyState.value = 'idle';
    }, 1000);
  } catch (error) {
    console.warn('Failed to copy account details:', error);
    copyState.value = 'idle';
  }
};
</script>

<template>
  <div class="bank-details-list" :class="{ 'bank-details-list--filled': filled }">
    <h3>{{ t('ui.bank_details.title') }}</h3>
    <div class="bank-details-list__fields">
      <div v-for="row in rows" :key="`${row.key}:${row.value}`" class="bank-details-list__row">
        <dl class="bank-details-list__content">
          <dt>{{ row.label }}</dt>
          <dd>{{ row.key.endsWith(':iban') ? formatIban(row.value) : row.value }}</dd>
        </dl>
        <UiCopyToggleButton
          :copy-text="row.value"
          :tooltip-text="row.label"
          :tooltip-success-text="t('ui.bank_details.copied')"
          :aria-label="t('ui.bank_details.copy_field', { field: row.label })"
          icon-dark
          icon-hover-background="var(--p-surface-100)"
        />
      </div>
    </div>
    <slot />
    <Button
      class="bank-details-list__copy"
      :label="
        t(copyState === 'success' ? 'ui.bank_details.copied' : 'ui.bank_details.copy_information')
      "
      :disabled="!rows.length || copyState === 'loading'"
      severity="primary"
      size="small"
      icon-pos="right"
      rounded
      @click="copyInformation"
    >
      <template #icon>
        <CopyIcon class="p-button-icon-right" />
      </template>
    </Button>
  </div>
</template>

<style scoped lang="scss">
.bank-details-list {
  display: flex;
  flex-direction: column;
  gap: $static-space-3;
  min-width: 0;
  color: var(--p-text-color);

  h3 {
    margin: 0;
    font-size: $text-lg;
    font-weight: $font-semibold;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: $static-space-3;
    margin: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: $static-space-2;
  }

  &__content {
    flex: 1;
    min-width: 0;
    margin: 0;

    dt {
      margin-bottom: $static-space-1;
      font-size: $text-xs;
      font-weight: $font-semibold;
    }

    dd {
      margin: 0;
      font-size: $static-text-md;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
  }

  &__copy {
    align-self: flex-end;
    font-size: $text-xs;
  }

  &--filled &__row {
    padding: $static-space-3;
    border-radius: var(--p-border-radius-lg);
    background-color: var(--p-form-field-filled-hover-background);
  }
}
</style>
